/*
  Scale · Sticky add-to-cart bar
  - Shows the bar once the main "Add to cart" button has scrolled above the viewport.
  - Clicking the bar clicks the main button, so Dawn's own product form handles the cart.
  - Keeps price, variant name and sold-out state in sync with the main product info.
*/
if (!customElements.get('scale-sticky-atc')) {
  customElements.define(
    'scale-sticky-atc',
    class ScaleStickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.stickyButton = this.querySelector('[data-sticky-button]');
        this.stickyPrice = this.querySelector('[data-sticky-price]');
        this.stickyVariant = this.querySelector('[data-sticky-variant]');

        this.stickyButton.addEventListener('click', () => this.getMainButton()?.click());

        this.observeMainButton();
        this.observeProductInfo();

        if (typeof subscribe === 'function') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId !== this.sectionId) return;
            if (this.stickyVariant && event.data.variant) {
              this.stickyVariant.textContent = event.data.variant.title;
            }
          });
        }
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        this.mutationObserver?.disconnect();
        this.unsubscribe?.();
        document.body.classList.remove('scale-sticky-atc-active');
      }

      getMainButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      // Show the bar when the main button is out of view *above* the screen.
      // A rAF-throttled scroll check is used instead of IntersectionObserver,
      // which can miss fast jumps (e.g. tapping a "#reviews" link).
      observeMainButton() {
        this.onScroll = () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            const mainButton = this.getMainButton();
            if (!mainButton) return;
            this.toggle(mainButton.getBoundingClientRect().bottom < 0);
          });
        };

        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }

      toggle(visible) {
        if (visible === this.isVisible) return;
        this.isVisible = visible;
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', String(!visible));
        this.stickyButton.tabIndex = visible ? 0 : -1;
        document.body.classList.toggle('scale-sticky-atc-active', visible);
      }

      // Dawn re-renders the price and buy button when the variant changes.
      // Watch the product info container and copy the new state into the bar.
      observeProductInfo() {
        const productInfo = document.getElementById(`ProductInfo-${this.sectionId}`);
        if (!productInfo) return;

        this.mutationObserver = new MutationObserver(() => this.sync());
        this.mutationObserver.observe(productInfo, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['disabled'],
        });
      }

      sync() {
        const mainButton = this.getMainButton();
        if (mainButton) {
          this.stickyButton.disabled = mainButton.disabled;
          const label = mainButton.querySelector('span')?.textContent.trim();
          if (label) this.stickyButton.textContent = label;
        }

        const price = document.querySelector(`#price-${this.sectionId} .price`);
        if (price && this.stickyPrice) {
          const current = price.classList.contains('price--on-sale')
            ? price.querySelector('.price__sale .price-item--last')
            : price.querySelector('.price__regular .price-item--regular');
          if (current) this.stickyPrice.textContent = current.textContent.trim();
        }
      }
    }
  );
}
